import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IntegrationPageContentProps } from '../IntegrationPage';
import { convertUTCToLocalDateTimeString } from 'internationalization/timezoneConversions';
import { useSettings } from 'services/settings/SettingsContext';

const MoneybirdIntegrationPageContent: React.FC<IntegrationPageContentProps> = ({
    response,
    handleRefetch
}) => {
    const { t } = useTranslation();
    const { userLocale, userTimezone } = useSettings();

    // Poll for initial import progress when initial import is running
    useEffect(() => {
        if (response?.initial_import_running) {
            const interval = setInterval(() => {
                handleRefetch();
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [response?.initial_import_running, handleRefetch]);

    return (
        <>
            {/* Integration information */}
            {response?.is_active && (
                <div className="integration-details">
                    <div className='header'>
                        {t(`integrations.general.info_title`)}:
                    </div>
                    <p>
                        {t(`integrations.general.${response.integration_name}.info_description`)}
                    </p>
                    {response?.export_job_to_invoice && (
                        <p style={{ marginBottom: "0.5rem" }}>
                            {t(`integrations.general.${response.integration_name}.export_job_to_invoice_description`)}
                        </p>
                    )}
                </div>
            )}

            {/* Initial import status */}
            {response?.initial_import_running && response?.import_sync_state?.length > 0 && (
                <div className="integration-details">

                    <div className='header'>
                        {t('integrations.general.initial_import_label')}
                    </div>

                    {response.import_sync_state?.map((entity: any) => {

                        if (entity.status === "completed") {
                            return (
                                <p key={entity.entity}>
                                    ✓ {t(`integrations.general.${response.integration_name}.${entity.entity}`)} {t('integrations.general.imported_label')}: ({entity.items_imported})
                                </p>
                            );
                        }
                        if (entity.status === "running") {
                            return (
                                <p key={entity.entity}>
                                    → {t(`integrations.general.${response.integration_name}.${entity.entity}`)} {t('integrations.general.importing_label')} ({entity.items_imported})
                                </p>
                            );
                        }

                        return (
                            <p key={entity.entity}>
                                {t(`integrations.general.${response.integration_name}.${entity.entity}`)} {t('integrations.general.pending_label')}
                            </p>
                        );
                    })}
                </div>
            )}

            {/* Export status */}
            {response?.export_job_to_invoice && (
                <div className="integration-details">

                    <div className='header'>
                        {t(`integrations.general.last_export`)}
                    </div>

                    {response?.export_job_to_invoice && (
                        <p>
                            {t('integrations.general.export_job_to_invoice_label')}: {new Date(convertUTCToLocalDateTimeString(response.last_export_at, userLocale, userTimezone)).toLocaleString()}
                        </p>
                    )}
                </div>
            )}

            {/* Integration settings */}
            {response?.export_job_to_invoice && (
                <div className="integration-details">
                    <div className='header'>
                        {t('integrations.general.integration_settings_label')}
                    </div>
                    <p>
                        {t('integrations.general.moneybird.administration_id_label')}: {response.administration_id}
                    </p>
                    <p>
                        {t('integrations.general.moneybird.default_workflow_label')}: {response.default_workflow_name}
                    </p>
                    <p>
                        {t('integrations.general.moneybird.default_ledger_account_label')}: {response.default_ledger_account_name}
                    </p>
                </div>
            )}
        </>
    );
};

export default MoneybirdIntegrationPageContent;
